import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import { Check, Clock, Sparkles, Plus, RefreshCw, Filter, ArrowRight, Lightbulb, ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";
import ViewSwitch from "../components/ViewSwitch";
import VersionBar from "../components/VersionBar";
import { agendaChoice } from "../utils/agenda";
import axios from "axios";
import { toast } from "sonner";
import { API, CAT_LABEL, placeImg } from "../utils/constants";
import { calcSmartDuration, formatDuration } from "../utils/smartDuration";
import BrandHeader from "../components/BrandHeader";
import PaymentModal from "./PaymentModal";
import PlaceDetailModal from "../components/PlaceDetailModal";
import { adaptPreviewToLegacy } from "../utils/expertPreviewAdapter";
import { FanMark, FanLoader } from "../components/FanMark";
import { PhotoCredit } from "../components/PhotoCredit";
import QuestionFlow from "../components/QuestionFlow";

// Only the current draft decision is submitted. Checking an experience again
// undoes its release; an untouched anonymous preview does not need sign-in.
export const updateRequestChoice = (decisions, contextId, requestId, included) => {
  const others = (decisions || []).filter(item => item.request_id !== requestId);
  return included ? others : [...others, { context_id: contextId, request_id: requestId, action: 'release' }];
};

// What they wrote, plus the places named in their screenshot — one line, the way the order sheet reads
// anything else they wrote (core/wishlist_sources on the server reads the sources). A saved page is not
// theirs to order from: its names ride beside the wishlist as ideas, welcome when they fit (brain9/ideas).
const namesOf = (places) => (places || []).map(p => (typeof p === "string" ? p : p?.name)).filter(Boolean);
// When the page asked them about a source (core/source_questions), what they ticked is theirs and the rest are
// ideas; a screenshot nobody was asked about joins the wishlist whole, as before.
export const wishlistWithSources = (text, userInput) => {
  const lines = [text || ""];
  const shot = userInput?.imageAsked ? [] : namesOf(userInput?.imagePlaces);
  if (shot.length) lines.push(`Places from my screenshot: ${shot.join(", ")}`);
  const mine = [...namesOf(userInput?.imageMusts), ...namesOf(userInput?.linkMusts)];
  if (mine.length) lines.push(`From what I saved, I don't want to miss: ${mine.join(", ")}`);
  return lines.filter(Boolean).join("\n\n");
};
export const ideasFromSources = (userInput) => [
  ...namesOf(userInput?.linkPlaces), ...(userInput?.imageAsked ? namesOf(userInput?.imagePlaces) : []),
];

// the badge classes the stylesheet defines (.zen-badge.food, .zen-badge.hidden_gem): "foodie" had no colour and
// "hidden" is the utility that hides an element, so Food badges were blank and Hidden-gem badges vanished
const CAT_CLASS_THUMB = { culture: "culture", food: "food", nightlife: "nightlife", special: "special", popular: "popular", hidden_gem: "hidden_gem" };

// Door-to-door travel times (hotel checkout → station → train → station → hotel checkin)
const TRAVEL_TIMES = {
  "Tokyo-Osaka": 4.5, "Tokyo-Kyoto": 4, "Osaka-Kyoto": 1.5,
  "Tokyo-Hiroshima": 5.5, "Osaka-Hiroshima": 3, "Kyoto-Hiroshima": 3.5,
  "Tokyo-Nara": 4.5, "Osaka-Nara": 1.5, "Kyoto-Nara": 1.5,
  "Tokyo-Hakone": 2.5, "Osaka-Hakone": 4.5,
};
const getTravelTime = (a, b) => {
  if (a === b) return 0;
  return TRAVEL_TIMES[`${a}-${b}`] || TRAVEL_TIMES[`${b}-${a}`] || 2;
};

// /expert/preview is now an async job — POST returns 202 + job_id, then
// poll /generation/status/{job_id} until done or error. This avoids
// Railway/Cloudflare's 60s proxy ceiling that was cutting the inline
// Selector call right around the day-grouped output threshold.
//
// Returns the preview response payload ({ profiler_brief, cities,
// day_brief }) on success. Throws on backend error or client timeout.
async function runPreviewJob(body, opts = {}) {
  return runJob("/expert/preview", body, opts);
}

// Step one of the preview (/expert/read): the wishlist read and the few questions it raises
// (backend core/questions). Its profiler_brief goes back to /expert/preview with the answers.
async function runReadJob(body, opts = {}) {
  return runJob("/expert/read", body, opts);
}

async function runJob(path, body, { onTick } = {}) {
  const session = localStorage.getItem("jf_session");
  const startRes = await axios.post(`${API}${path}`, body, { timeout: 30000, headers: session ? { Authorization: `Bearer ${session}` } : {} });
  const jobId = startRes?.data?.job_id;
  if (!jobId) {
    throw new Error("Preview job did not return a job_id");
  }

  // WALL-CLOCK deadline — bounded total time regardless of how long
  // individual polls take. Backend caps preview at 360s (scaled up for
  // long multi-city trips that need bigger Selector outputs). Add 30s
  // of slack and hard-fail at 390s.
  const deadline = Date.now() + 390000;
  const intervalMs = 2000;
  let consecutiveFailures = 0;
  let i = 0;
  while (Date.now() < deadline) {
    await new Promise(r => setTimeout(r, intervalMs));
    if (typeof onTick === "function") onTick(i++);
    let poll;
    try {
      // Short per-call timeout — status endpoint is an in-memory dict
      // lookup, should never take more than a couple seconds.
      poll = await axios.get(`${API}/generation/status/${jobId}`, { timeout: 8000 });
    } catch (e) {
      if (e?.response?.status === 404) {
        throw new Error("Preview job not found (server may have restarted mid-job)");
      }
      consecutiveFailures++;
      if (consecutiveFailures >= 5) {
        throw new Error("Preview status endpoint unreachable");
      }
      continue;
    }
    consecutiveFailures = 0;
    const data = poll?.data || {};
    if (data.generation_status === "done") {
      return data;
    }
    if (data.generation_status === "error") {
      const err = new Error(data.error || "Preview job failed");
      err.fromServer = Boolean(data.error);   // written for the traveller (e.g. "on our side, nothing charged")
      throw err;
    }
    // still generating — continue polling
  }
  throw new Error("Preview took too long. Please try again.");
}

const SummaryPage = ({
  onNext, onBack, userInput, extractedPlaces, setExtractedPlaces,
  selectedPlaces, setSelectedPlaces, selectedCities, dates, startOver,
  selectedVibes, tripConfig, setTripConfig, navigateTo,
  userEmail, setUserEmail, userCredits, setUserCredits, tripPace,
  sessionToken, onSignIn,
  // Traveler profile from onboarding (used by stage1 call)
  travelerType, kidAges, isFirstTimer, dayStartLabel, dayEndLabel,
  // Pipeline state lifted to App.js so selection + city hours persist
  // across Summary ↔ Catalog navigation.
  profilerBrief, setProfilerBrief,
  curatorBrief, setCuratorBrief,
  // AI-derived state also lifted (Class 5 of the architecture review):
  // these previously lived in SummaryPage's local useState, which meant
  // a Catalog → Summary remount wiped them and re-fired the heavy
  // preview Selector call. Now they persist and the useEffect noops
  // when nothing changed.
  aiAnalyses = {}, setAiAnalyses = () => {},
  previewPicks = [], setPreviewPicks = () => {},
  dayBrief = null, setDayBrief = () => {},
  // what they answered when we asked (core/questions), kept for every later reading
  questionAnswers = {}, setQuestionAnswers = () => {},
  // the practical page (diet, allergies, needs, what to skip, how they eat)
  practical = {},
  draftId = null,
  // the agenda step (the server says whether it is on): the days come back to be shaped before they are written
  agendaStep = false, onAgenda, agendaDays = null, onOpenAgenda, writeNow = false, clearWriteNow, payNow = false, clearPayNow,
  refundAckText = "", onNewVersion,
}) => {
  const [wishMore, setWishMore] = useState("");          // "Add to your wishlist", in their own words (2.2 a)
  const [refundAsk, setRefundAsk] = useState(null);        // before the first detail they see: "calendar" | "trip" (docs/producto 2.4)
  const [refundTicked, setRefundTicked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // "this temple in the morning" (owner, 2026-10-21): dawn or after dark, place by place, chosen here with a tap
  // and applied by the server when the trip is built ({name: moment}; core/best_moment.preference)
  const [momentChoice, setMomentChoice] = useState({});
  const chooseMoment = (name, moment) => setMomentChoice(prev => {
    const next = { ...prev };
    if (moment) next[name] = moment; else delete next[name];
    return next;
  });
  // the questions of this reading, shown one at a time; the preview waits for the answers
  const [pendingQuestions, setPendingQuestions] = useState(null);
  const answerRef = useRef(null);
  const [loadingMsg, setLoadingMsg] = useState("Reading your wishlist...");
  const [refreshingInsights, setRefreshingInsights] = useState(false);
  const [refreshingRecs, setRefreshingRecs] = useState(false);
  const [cityFilter, setCityFilter] = useState("all");
  const [showOnlyPicks, setShowOnlyPicks] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isCheckingCredits, setIsCheckingCredits] = useState(false);
  const genLockRef = useRef(false);
  const [generateError, setGenerateError] = useState(null);
  // what they picked in the catalog before this page, when they came that way: it stays their wishlist
  const pickedFromCatalog = useRef(null);
  const catalogWishlist = () => {
    const picked = pickedFromCatalog.current || [];
    return [userInput?.text || "", picked.length ? `Places I picked from your catalog: ${picked.join(", ")}` : ""]
      .filter(Boolean).join("\n\n") || null;
  };
  const [genProgress, setGenProgress] = useState("");
  // Live reveal: {stage, days:[{day_number,city,date}], titles:{n:title}}
  // streamed from the backend while the pipeline runs — day titles appear
  // one by one as each day's voice lands. The wait becomes the show.
  const [genReveal, setGenReveal] = useState(null);
  const [showPayment, setShowPayment] = useState(false);
  const [detailPlace, setDetailPlace] = useState(null);

  // Review mode: true on first load, user has ONE chance to add text and
  // click "Update my selection" (or click "Looks good" to skip). After a
  // successful Update or a Looks good click, review mode is exited
  // permanently for this session — there is no re-entry path in the UI.
  // If the user wants more places after exiting review, they use the
  // catalog browser. Failed Update attempts (API errors) do NOT burn the
  // chance — the user can retry from the still-visible review panel.
  //
  // Survives catalog round-trips: if curatorBrief already exists on mount,
  // the user has been through stage1+stage2 and review is done.

  // previewPicks + dayBrief now come from App.js props (Step F lift).
  // Remember when the heavy preview call failed so a Catalog→Summary
  // remount doesn't silently re-fire the heavy AI call. Stays sticky
  // until the user clicks Retry or input changes.
  const [previewFailed, setPreviewFailed] = useState(false);

  // Clarifications were Gen-1 ambiguity modals from the curator path.
  // Removed in A.3 — the Expert doesn't ask clarifying questions; if it
  // can't honour something, it surfaces a note in traveler_notes on the
  // final trip.

  // Stable key that changes when the user's trip inputs change
  const inputKey = useMemo(() => {
    const obj = {
      cities: (selectedCities || []).map(c => `${c.name}:${c.days}`),
      vibes: selectedVibes || [],
      practical: practical || {},
      text: userInput?.text || "",
      url: userInput?.url || "",
      imgCount: (userInput?.imagePlaces || []).length,
      linkCount: (userInput?.linkPlaces || []).length,
      mine: [...(userInput?.imageMusts || []), ...(userInput?.linkMusts || [])].join(","),
      start: dates?.startDate || "",
      end: dates?.endDate || "",
      pace: tripPace || "balanced",
      dayStart: dayStartLabel || "",
      dayEnd: dayEndLabel || "",
      traveler: travelerType || "",
      firstTimer: isFirstTimer,
      kids: (kidAges || []).join(","),
    };
    return JSON.stringify(obj);
  }, [selectedCities, selectedVibes, practical, userInput, dates?.startDate, dates?.endDate, tripPace, dayStartLabel, dayEndLabel, travelerType, isFirstTimer, kidAges]);

  const lastLoadedKey = useRef(null);

  // ===== Time calculations (needed before smart-plan call) =====
  const tripDays = (() => { if (!dates?.startDate || !dates?.endDate) return 0; const d = Math.ceil((new Date(dates.endDate) - new Date(dates.startDate)) / 864e5) + 1; return d > 0 ? d : 0; })();
  const arrH = parseInt(dates?.arrivalTime?.split(":")[0] || "14");
  const depH = parseInt(dates?.departureTime?.split(":")[0] || "18");
  const isLateArr = arrH >= 22 || arrH < 3;
  const sortedCities = (selectedCities || []);

  // Legacy local calculator — kept ONLY as fallback for paths where curatorBrief
  // is missing (e.g. /summary/smart-plan legacy flow, or first load before stage2
  // completes). It uses a hardcoded 12h/day window that does NOT respect the
  // user's pace or day_start/day_end labels. Never use this directly for the
  // visible time bar — use getCityHours() instead.
  const calcCityTime = useCallback(() => {
    return sortedCities.map((c, idx) => {
      const days = c.days || 1;
      const isFirst = idx === 0;
      const isLast = idx === sortedCities.length - 1;
      let availH = days * 12;
      if (isFirst) { const readyH = isLateArr ? 9 : Math.min(arrH + 4, 21); availH = Math.max(0, 21 - readyH) + (days - 1) * 12; }
      if (isLast && !isFirst) { availH = (days - 1) * 12 + Math.max(0, Math.max(depH - 4, 6) - 9); }
      if (isFirst && isLast && days === 1) { availH = Math.max(0, Math.max(depH - 4, 6) - (isLateArr ? 9 : Math.min(arrH + 4, 21))); }
      if (idx < sortedCities.length - 1) availH -= getTravelTime(c.name, sortedCities[idx + 1].name);
      return { name: c.name, availH: Math.max(0, availH) };
    });
  }, [sortedCities, arrH, depH, isLateArr]);

  // Single source of truth for "how many hours does this city have".
  // Reads from CuratorBrief (backend-computed, respects pace + day labels)
  // and falls back to calcCityTime() only if the brief is missing.
  const getCityHours = useCallback((cityName) => {
    const fromBrief = curatorBrief?.cities?.[cityName]?.available_hours;
    if (fromBrief != null) return fromBrief;
    const ctInfo = calcCityTime().find(ct => ct.name === cityName);
    return ctInfo?.availH || 0;
  }, [curatorBrief, calcCityTime]);

  // ===== Load plan when inputs change =====
  useEffect(() => {
    // Selection persists across Summary ↔ Catalog navigation.
    // If the user already has places loaded AND inputs are unchanged, noop
    // (just populate analyses if missing).
    if (extractedPlaces.length > 0 && lastLoadedKey.current === inputKey) {
      if (Object.keys(aiAnalyses).length === 0) runAnalysisOnly();
      return;
    }
    // If the preview already failed once on this input key, DO NOT
    // silently re-fire the heavy Selector call on a remount (e.g. user
    // navigated to Catalog and came back). Show the retry CTA instead.
    if (previewFailed && lastLoadedKey.current === inputKey) {
      return;
    }
    lastLoadedKey.current = inputKey;

    // Inputs changed (or first load): preserve selection if any, else run full pipeline
    if (extractedPlaces.length > 0 && profilerBrief) {
      // Restored from a Stripe-redirect snapshot (or catalog round-trip):
      // places AND insights both survived — don't re-fire the heavy
      // preview call just to recompute insights we already have.
      if (Object.keys(aiAnalyses).length === 0) runAnalysisOnly();
    } else if (extractedPlaces.length > 0) {
      // They came through the catalog ("Browse places instead"): what they picked IS their wishlist. The
      // full preview runs with those names written into it, so the trip has a profile and can be generated
      // (2026-10-07: this path never had one and "Generate" always failed).
      pickedFromCatalog.current = (selectedPlaces || []).filter(Boolean);
      loadFullPipeline(catalogWishlist());
    } else {
      // First load: the preview
      loadFullPipeline();
    }
  }, [inputKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // What every reading of the trip is asked with — the onboarding, the wishlist and the answers they gave.
  const previewBody = (userText, cityDays) => ({
    user_text: userText,
    city_days: cityDays,
    bases: Object.fromEntries((selectedCities || []).filter(c => c && c.name && (c.base || "").trim()).map(c => [c.name, c.base.trim()])),
    vibes: selectedVibes || [],
    pace: tripPace || "balanced",
    traveler_type: travelerType || null,
    // what they answered on the traveller page (2026-10-07: asked, then never sent)
    is_first_timer: typeof isFirstTimer === "boolean" ? isFirstTimer : null,
    kid_ages: kidAges || [],
    ideas: ideasFromSources(userInput),
    day_start_label: dayStartLabel || "",
    day_end_label: dayEndLabel || "",
    unfold_mode: userInput?.unfoldMode || false,
    arrival_date: dates?.startDate || "",
    departure_date: dates?.endDate || "",
    arrival_time: dates?.arrivalTime || "09:00",
    departure_time: dates?.departureTime || "21:00",
    airport: dates?.airport || "NRT",
    departure_airport: dates?.differentDepartureAirport ? (dates?.departureAirport || null) : null,
    return_to_airport_city: dates?.returnToAirportCity !== false,
    answers: questionAnswers || {},
    practical: practical || {},
  });

  // ===== EXPERT PREVIEW (A.2): one Gen-3 call replaces the old chain =====
  // Was: Profiler (/pipeline/stage1) → Curator (/pipeline/stage2) →
  //      separate analyze call (/summary/analyze)
  //      → curator picks places → user reviews → "build" → Expert re-decides
  //
  // Now: /expert/preview runs Profiler + ONE small Expert (Claude) call
  //      that returns per-city preview lines + anchor places, in the same
  //      voice that will write the full trip. No seam, no double-brain.
  //
  // When expandedText is provided (review-mode update), it's concatenated
  // with the user's original wishlist so the Profiler re-extracts new
  // sacred requests / walls / dietary against the combined text.
  const loadFullPipeline = async (expandedText = null) => {
    setIsLoading(true);
    // The first line is said once; then the friend keeps you company while
    // the places are chosen (the owner's call, 2026-10-05: warm, never robotic,
    // never the opening line again).
    const openingMsg = "Reading your wishlist...";
    const loadingMsgs = [
      "Thinking about where your mornings should begin...",
      "Looking for the quiet corners next to the famous ones...",
      "Checking what's best early, and what's best after dark...",
      "Finding the spots a local would send a friend to...",
      "Making sure nothing you asked for gets left behind...",
      "Matching each city to the kind of days you enjoy...",
      "Weighing a few good options for each day...",
      "Nearly there — laying it all out for you...",
    ];
    let msgIdx = -1;
    setLoadingMsg(openingMsg);
    const msgInterval = setInterval(() => {
      msgIdx = (msgIdx + 1) % loadingMsgs.length;
      setLoadingMsg(loadingMsgs[msgIdx]);
    }, 4500);

    try {
      const cityDays = {};
      for (const c of sortedCities) {
        if (c.name) cityDays[c.name] = c.days || 3;
      }
      const userText = wishlistWithSources(expandedText || userInput?.text || "", userInput);
      const body = previewBody(userText, cityDays);

      // Step one: read the wishlist; the questions it raises are asked one at a time, then the places are chosen
      // from that same reading with the answers (never read twice). An older server without the step: one call.
      let previewData;
      let read = null;
      try {
        read = await runReadJob(body);
      } catch (e) {
        if (![404, 405].includes(e?.response?.status)) throw e;
      }
      if (read) {
        let answers = { ...(questionAnswers || {}) };
        if ((read.questions || []).length) {
          setIsLoading(false);
          const given = await new Promise(resolve => { answerRef.current = resolve; setPendingQuestions(read.questions); });
          setPendingQuestions(null);
          // "Add that city to my trip": back to the cities, the reading starts again from there
          const adding = read.questions.filter(q => q.kind === "other_city" && given[q.key] === "add_city");
          if (adding.length) {
            const rest = Object.fromEntries(Object.entries(given).filter(([k]) => !adding.some(q => q.key === k)));
            setQuestionAnswers({ ...(questionAnswers || {}), ...rest });
            toast(`Add ${adding.map(q => q.subject?.city).filter(Boolean).join(" and ")} to your cities — we'll pick up from there.`);
            clearInterval(msgInterval);
            setIsLoading(false);
            lastLoadedKey.current = null;
            if (navigateTo) navigateTo("cities");
            return;
          }
          answers = { ...answers, ...given };
          setQuestionAnswers(answers);
          setLoadingMsg("Choosing your places...");
          setIsLoading(true);
        }
        previewData = await runPreviewJob({ ...body, read: read.profiler_brief, answers });
      } else {
        previewData = await runPreviewJob(body);
      }

      const { profilerBrief, curatorBrief, places, allNames, analyses, previewPicks: picks, dayBrief: db } = adaptPreviewToLegacy(previewData);
      if (!profilerBrief) throw new Error("Preview returned no profiler_brief");

      if (setProfilerBrief) setProfilerBrief(profilerBrief);
      if (setCuratorBrief) setCuratorBrief(curatorBrief);
      setExtractedPlaces(places);
      setSelectedPlaces([...new Set(allNames)]);
      setPreviewPicks(picks || []);
      setDayBrief(db || null);
      // Preview lines come back in the same call — no flash, no second AI hop.
      setAiAnalyses(analyses);
      setRefreshingInsights(false);
      setPreviewFailed(false);

    } catch (e) {
      // Surface detailed error so it's diagnosable. Backend detail takes
      // precedence (that's the actual stage1/stage2 failure message).
      const detail = e?.response?.data?.detail
        || e?.response?.data?.error
        || e?.message
        || "Unknown error";
      const status = e?.response?.status || "";
      if (status === 401) {
        // their session ended (or they came back to this page signed out): ask for the email, then Retry
        setPreviewFailed(true);
        onSignIn?.();
        return;
      }
      if (status === 402) {
        // the free glance is had (core/allowance): planning is the Trip Planner
        setPreviewFailed(true);
        setShowPayment(true);
        return;
      }
      console.error("Pipeline failed:", { detail, status, url: e?.config?.url, response: e?.response?.data, error: e });
      // Short toast for the user, full detail in a non-blocking followup
      toast.error(e?.fromServer ? e.message
        : `Something went wrong building your selection. ${status ? `(${status})` : ""} Please try again.`);
      // Log a second detailed entry that's easy to screenshot from DevTools
      console.error("[JapanUnfolded] Pipeline error detail:", detail);
      if (typeof window !== "undefined") {
        window.__jf_last_error = { detail, status, url: e?.config?.url, response: e?.response?.data };
      }
      // Mark as failed so a remount (e.g. Catalog → Summary) doesn't
      // silently re-fire the heavy call. User clicks Retry to try again.
      setPreviewFailed(true);
    } finally {
      // ALWAYS clear the spinner + interval, success or failure. Pre-existing
      // bug: success path used `return;` to skip these, so the page kept
      // showing the "Choosing the anchor spots..." spinner even after data
      // arrived. Only surfaced now because async-poll preview takes long
      // enough for the user to notice.
      clearInterval(msgInterval);
      setIsLoading(false);
    }
  };

  // Retry handler invoked from the empty-state CTA.
  const handleRetryPreview = async () => {
    setPreviewFailed(false);
    await loadFullPipeline();
  };

  // ===== MORE IDEAS =====
  // Runs the Expert preview against the current wishlist: on load when
  // there are no insights yet, and for "More ideas" (recommendations
  // only). The insights written first stay — the owner's call
  // (2026-10-05): the trip's own story is written from the final picks.
  // The user's current selection is preserved.
  const runAnalysisOnly = async ({ updateActions = true, updateRecs = true } = {}) => {
    setRefreshingInsights(updateActions);
    try {
      const cityDays = {};
      sortedCities.forEach(c => { if (c.name) cityDays[c.name] = c.days || 3; });

      const previewData = await runPreviewJob(previewBody(wishlistWithSources(userInput?.text || "", userInput), cityDays));
      const { analyses, curatorBrief: newCb } = adaptPreviewToLegacy(previewData);
      // Decoupled updates: "More ideas" must NOT rewrite the 3 city
      // bullets (watching your bullets shuffle after adding places reads
      // as AI slop and kills trust). Merge per surface.
      setAiAnalyses(prev => {
        const merged = {};
        const cities = new Set([...Object.keys(prev || {}), ...Object.keys(analyses || {})]);
        for (const c of cities) {
          merged[c] = {
            actions: updateActions
              ? (analyses[c]?.actions || prev?.[c]?.actions || [])
              : (prev?.[c]?.actions || analyses[c]?.actions || []),
            recommendations: updateRecs
              ? (analyses[c]?.recommendations || prev?.[c]?.recommendations || [])
              : (prev?.[c]?.recommendations || analyses[c]?.recommendations || []),
          };
        }
        return merged;
      });
      // Update hours map too, in case dates / pace changed since last run.
      if (setCuratorBrief) setCuratorBrief(newCb);
    } catch (e) {
      if (e?.response?.status === 402) {
        // the free glance is had: asked for ("More ideas"), the plans open; on its own, nothing is said
        if (!updateActions) setShowPayment(true);
        return;
      }
      console.error("Insights preview failed", e);
      toast.error("Couldn't refresh right now — your current picks are untouched. Try again in a minute.");
    } finally {
      setRefreshingInsights(false);
    }
  };

  // ===== Selection handlers =====
  const togglePlace = (name) => {
    // FIX #5: Only toggle selection, never remove from extractedPlaces
    setSelectedPlaces(prev => prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]);
  };

  const toggleRecommendation = (place) => {
    // Only ADD (one-way: from recs to plan)
    if (!selectedPlaces.includes(place.name)) {
      if (!extractedPlaces.find(p => p.name === place.name)) {
        setExtractedPlaces(prev => [...prev, place]);
      }
      setSelectedPlaces(prev => [...prev, place.name]);
    }
  };

  // ===== Computed =====
  const activeList = extractedPlaces.filter(p => selectedPlaces.includes(p.name));
  const FOOD_CATS = new Set(["food", "foodie"]);
  const activityCount = activeList.filter(p => !FOOD_CATS.has((p.category || "").toLowerCase())).length;
  const foodCount = activeList.length - activityCount;
  // Header "spots" = TOTAL selected (matches the per-city '6 spots' counts
  // shown on each city card). Previously this excluded food places, which
  // made the header read '9 spots' while the per-city cards summed to 12 —
  // a confusing mismatch. Time bars (~Xh visits) still measure activities
  // only; food doesn't add to time math because meals fit between blocks.
  const selectedCount = activeList.length;

  const cityTimeData = sortedCities.map((c, idx) => {
    const cityPlaces = activeList.filter(p => p.city === c.name);
    const cityFoodCount = cityPlaces.filter(p => FOOD_CATS.has((p.category || "").toLowerCase())).length;
    const cityActivityCount = cityPlaces.length - cityFoodCount;
    const smart = calcSmartDuration(cityPlaces);
    // Read from curatorBrief (backend-computed, respects pace + day labels)
    // via the getCityHours helper. Falls back to calcCityTime() if missing.
    const availH = getCityHours(c.name);
    const freeH = availH - smart.totalNeeded;
    const travelOut = idx < sortedCities.length - 1 ? getTravelTime(c.name, sortedCities[idx + 1].name) : 0;
    // Infeasibility: city has time allocated but the wishlist didn't produce
    // enough content to fill it. Most common trigger: 1-day city with zero
    // matching cards ("4 days: Tokyo 2, Kyoto 1, Osaka 1" → 0 places in Osaka).
    const isEmpty = cityPlaces.length === 0 && availH > 0;
    const isUnderfilled = !isEmpty && availH >= 6 && cityPlaces.length <= 1 && smart.totalNeeded < 2;
    // How full the city is, by the brain's own measure: the places to see that its
    // days hold (the server's day brief — about three a full day). Raw hours misled:
    // an early-bird-and-night-owl day runs 08:00-23:00, and a list of visit minutes
    // left "17h free" in a Kyoto whose days were already full (owner, 2026-10-05).
    const target = Math.max(1, dayBrief?.cities?.[c.name]?.suggested_picks || (c.days || 1) * 3);
    const fill = cityActivityCount / target;
    return {
      name: c.name, days: c.days, count: cityPlaces.length,
      foodCount: cityFoodCount, activityCount: cityActivityCount,
      availH: Math.round(availH), neededH: Math.round(smart.totalNeeded),
      durationH: Math.round(smart.totalDuration), transitH: Math.round(smart.transitHours),
      freeH: Math.round(freeH), target, fill, isOver: fill >= 1.35, travelOut,
      isEmpty, isUnderfilled, isUnder: isEmpty || isUnderfilled,
    };
  });

  const anyOverTime = cityTimeData.some(c => c.isOver);
  const anyUnderTime = cityTimeData.some(c => c.isUnder);
  const underCities = cityTimeData.filter(c => c.isUnder);

  // FIX #5: Show ALL extracted places (selected + deselected), deselected are dimmed
  const visiblePlaces = extractedPlaces.filter(p => {
    if (cityFilter !== "all" && p.city !== cityFilter) return false;
    if (showOnlyPicks && !p.from_wishlist) return false;
    return true;
  });

  const allRecs = Object.values(aiAnalyses).flatMap(a => a.recommendations || [])
    .filter(r => !selectedPlaces.includes(r.name))
    .filter(r => cityFilter === "all" || r.city === cityFilter);

  // Generate (with credit check). `until`: "trip" — the main way, the written itinerary, as always; "calendar" — the
  // option beside it (owner, 2026-11-04): the same days as an agenda to see, shape and play with before it is written.
  const wantedRef = useRef("trip");
  const handleGenerateClick = (until = "trip") => {
    // Ref guard blocks a fast double-tap from firing two generation jobs
    // (each job deducts a credit). The button is also disabled while this
    // is in flight; once doGenerate runs, isGenerating hides the button.
    if (genLockRef.current || isGenerating) return;
    if ((profilerBrief?._request_decisions || []).length && !sessionToken) {
      onSignIn?.();
      return;
    }
    if (!userEmail) {
      setShowPayment(true);
      return;
    }
    wantedRef.current = until;
    genLockRef.current = true;
    setIsCheckingCredits(true);
    const release = () => { genLockRef.current = false; setIsCheckingCredits(false); };
    // The server owns the gate: a credit → full trip; a first-ever email →
    // locked teaser (Day 1 visible); otherwise 402 → the plans. Refresh the
    // credit display in the background, then just try to generate.
    axios.get(`${API}/credits/check?email=${encodeURIComponent(userEmail)}`)
      .then(res => setUserCredits(res.data.credits))
      .catch(() => {});
    Promise.resolve(doGenerate(until)).finally(release);
  };

  const handleCreditsReady = (email, credits) => {
    setUserEmail(email);
    setUserCredits(credits);
    setShowPayment(false);
    doGenerate(wantedRef.current);               // what they were going for when the plans opened
  };

  // free, from the shape of their days: the plans
  useEffect(() => {
    if (!payNow) return;
    clearPayNow?.();
    setShowPayment(true);
  }, [payNow]); // eslint-disable-line react-hooks/exhaustive-deps

  // "Generate itinerary" on the calendar: the trip is written here, with the usual wait, from the latest of both views
  useEffect(() => {
    if (!writeNow) return;
    clearWriteNow?.();
    doGenerate("trip");
  }, [writeNow]); // eslint-disable-line react-hooks/exhaustive-deps

  // `until`: "calendar" asks for the agenda (the days, before a word is written), "trip" for the written trip — from
  // the selection as it stands and, when they laid it out on the calendar, each place on its day (utils/agenda).
  const doGenerate = async (until = "trip", refundAck = false) => {
    if ((profilerBrief?._request_decisions || []).length && !sessionToken) {
      onSignIn?.();
      return;
    }
    setIsGenerating(true);
    setGenerateError(null);
    setGenReveal(null);
    setGenProgress("Preparing your trip...");
    try {
      // Defensive: profilerBrief + curatorBrief must exist from stage1+2
      if (!profilerBrief || !curatorBrief) {
        throw new Error("Trip profile not ready — please go back and try again.");
      }

      setGenProgress("Gathering everything you told us...");
      // Build the picks payload from what the user ACTUALLY selected —
      // not just the preview's picks. Three sources merge here:
      //   1. Preview picks the user kept checked (full AI data rides along)
      //   2. Places hand-added from the catalog browser
      //   3. Places added from "You might also love" / detail modal
      // Deselections are honoured (not in selectedPlaces → not sent).
      // Hand-added places are flagged user_added: the backend treats them
      // like sacred requests — guaranteed placed or honestly explained.
      // (Previously only previewPicks were sent, which silently DROPPED
      // every hand-picked place at generation time.)
      const selectedSet = new Set(selectedPlaces || []);
      const pickByName = new Map(
        (previewPicks || []).filter(p => p && p.name).map(p => [p.name.toLowerCase(), p])
      );
      const filteredPicks = [];
      const seenNames = new Set();
      for (const place of (extractedPlaces || [])) {
        if (!place?.name || !selectedSet.has(place.name)) continue;
        const key = place.name.toLowerCase();
        if (seenNames.has(key)) continue;
        seenNames.add(key);
        const fromPreview = pickByName.get(key);
        if (fromPreview) {
          filteredPicks.push(fromPreview);
        } else {
          // Hand-added (catalog browse / recs / detail modal). The backend
          // re-matches to the catalog card by name for durations, images
          // and data — this payload just has to carry identity + the flag.
          filteredPicks.push({
            name: place.name,
            city: place.city || "",
            neighborhood: place.neighborhood || "",
            category: (place.category || "").toUpperCase(),
            why: "",
            duration_h: typeof place.duration === "number" ? place.duration : 1.5,
            from_catalog: place.from_catalog !== false,
            is_sacred: false,
            meal_role: null,
            description: "",
            tip: "",
            user_added: true,
          });
        }
      }
      // the latest of the two views: what is selected (the glance) and when (the calendar, if they laid it out)
      const laidOut = until === "trip" && agendaDays
        ? agendaChoice(agendaDays, filteredPicks, new Set((extractedPlaces || []).map((p) => String(p.name || "").toLowerCase())))
        : null;
      const res = await axios.post(`${API}/itinerary/${until === "calendar" ? "calendar" : "generate-from-pipeline"}`, {
        profiler_brief: Object.keys(momentChoice).length ? { ...profilerBrief, _moment_choice: momentChoice } : profilerBrief,
        curator_brief: curatorBrief,
        preview_picks: laidOut ? laidOut.picks : filteredPicks,
        agenda: laidOut ? laidOut.pins : undefined,
        refund_ack: refundAck || undefined,      // they ticked that the refund ends at their first calendar
        preview_token: profilerBrief?._preview_token,
        request_decisions: profilerBrief?._request_decisions || [],
        day_brief: dayBrief,
        user_email: userEmail || "",
        draft_id: draftId || "",          // the trip in the making it comes from: "change the plan" opens it again
        start_date: dates.startDate,
        arrival_time: dates.arrivalTime || "09:00",
        departure_time: dates.departureTime || "21:00",
        // one brain for everyone since 2026-10-07; the field stays for the server's old check
        brain: "v9",
      }, { timeout: 60000, headers: localStorage.getItem("jf_session")
        ? { Authorization: `Bearer ${localStorage.getItem("jf_session")}` } : {} });
      const jobId = res.data.job_id;
      if (!jobId) throw new Error("No job ID returned");

      // Progress messages — aligned with the new pipeline stages
      const progressMsgs = until === "calendar" ? [
        "Gathering everything you told us...", "Putting each day in a sensible order...",
        "Finding lunch and dinner close to where you'll be...", "Laying out your days..."
      ] : [
        "Gathering everything you told us...", "Putting each day in a sensible order...",
        "Finding lunch and dinner close to where you'll be...", "Writing your days, one at a time...",
        "Reading it over, the way a friend would...", "Nearly there..."
      ];

      // Poll for the result. A slow or lost answer is not a failure: the trip keeps being written on the
      // server (and is saved to My Trips when it is done), so the page keeps asking until a wall-clock
      // deadline. Only the server saying "error" stops it (2026-10-07: one 20-second answer during a
      // 15-day trip made the page give up while the trip was finished and saved).
      let result = null;
      let lost = 0;
      let hadSuccessfulPoll = false;
      const deadline = Date.now() + 10 * 60 * 1000;
      for (let i = 0; Date.now() < deadline; i++) {
        setGenProgress(progressMsgs[Math.min(Math.floor(i / 4), progressMsgs.length - 1)]);
        await new Promise(r => setTimeout(r, 3000));
        let poll;
        try {
          poll = await axios.get(`${API}/generation/status/${jobId}`, { timeout: 20000 });
        } catch (pollErr) {
          // the job is gone after we had seen it (a restart, or the result already handed over)
          if (pollErr.response?.status === 404 && hadSuccessfulPoll && ++lost > 15) {
            const gone = new Error("Your trip may already be finished — open My Trips to find it.");
            gone.fromServer = true;
            throw gone;
          }
          continue;   // slow, offline for a moment, a proxy hiccup: ask again
        }
        hadSuccessfulPoll = true;
        lost = 0;
        if (poll.data.progress) setGenReveal(poll.data.progress);
        if (poll.data.generation_status === "done" || poll.data.days) {
          result = poll.data;
          break;
        }
        if (poll.data.generation_status === "error") {
          const failed = new Error(poll.data.error || "Generation failed");
          failed.fromServer = true;
          throw failed;
        }
      }
      if (!result) throw new Error("Your trip is taking longer than usual. It is still being written and will appear in My Trips when it is ready — no need to generate it again.");
      if (until === "calendar") {
        onAgenda?.(result.agenda || result.shape ? result : { agenda: result });
        return;
      }

      // Credits deducted server-side — refresh count
      if (result.credit_deducted && userEmail) {
        axios.get(`${API}/credits/check?email=${encodeURIComponent(userEmail)}`)
          .then(r => setUserCredits(r.data.credits))
          .catch(() => {});
      }
      setTripConfig({ itinerary: result, arrival_time: dates.arrivalTime, departure_time: dates.departureTime });
      navigateTo("result");
    } catch (e) {
      // Server-side paid gate (402): the email has no credits and its one
      // teaser is already used — route to the plans, not an error.
      if (e.response?.status === 402) {
        setIsGenerating(false);
        // the server says why (the one preview already used, or today's free previews all taken)
        const why = e.response?.data?.detail;
        // "plan_required" is the server's word for "the plans open now": nothing to read, the plans say it
        if (typeof why === "string" && why && why !== "plan_required") setGenerateError(why);
        setShowPayment(true);
        return;
      }
      let msg;
      const detail = e.response?.data?.detail || e.message || "";
      const detailLower = detail.toLowerCase();
      if (e.response?.status === 409 && detail === "refund_ack_required") {
        // their first calendar: they say they know the refund ends there, then they see it
        setIsGenerating(false);
        setRefundAsk(until);              // which they were going for: the calendar or the itinerary
        return;
      }
      if (e.response?.status === 409 && /preview|refresh/.test(detailLower)) {
        // the places were chosen more than a day ago, or the trip changed since: choose them again with the
        // same inputs instead of re-sending what the server refused (2026-10-07: "Try again" looped forever)
        setGenerateError("Your places were chosen a while ago, so we refreshed them — take a look, then generate your trip.");
        setIsGenerating(false);
        loadFullPipeline(pickedFromCatalog.current ? catalogWishlist() : null);
        return;
      }
      if (detailLower.includes("budget") || detailLower.includes("balance") || detailLower.includes("exceeded")) {
        msg = "Generation failed. Please try again.";
      } else if (e.code === 'ECONNABORTED') {
        msg = "The AI is taking longer than usual. Please try again.";
      } else {
        msg = detail || "Generation failed. Please try again.";
      }
      setGenerateError(msg);
    } finally { setIsGenerating(false); }
  };

  if (pendingQuestions) return (
    <QuestionFlow questions={pendingQuestions} onDone={(given) => answerRef.current && answerRef.current(given)} />
  );

  if (isLoading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--zen-bg)" }}>
      <div className="text-center px-8 max-w-sm">
        {/* the fan folds and unfolds while the places are chosen — same as the trip's wait */}
        <div style={{ margin: "0 auto 28px" }}><FanLoader size={112} /></div>
        <h3 style={{ fontFamily: '"DM Sans", system-ui, sans-serif', fontWeight: 700, fontSize: 20, color: "var(--zen-text)" }}>{loadingMsg}</h3>
        <p className="font-mono-label" style={{ fontSize: 11, letterSpacing: "0.16em", marginTop: 14, color: "var(--zen-dim)" }}>This takes about a minute — we're choosing, not templating</p>
      </div>
    </div>
  );

  if (refundAsk) return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "var(--zen-bg)" }} data-testid="refund-ack">
      <div className="w-full max-w-md p-6" style={{ background: "var(--zen-s1)", border: "1px solid var(--zen-border)", borderRadius: 8 }}>
        <h3 style={{ fontFamily: '"DM Sans", system-ui, sans-serif', fontWeight: 800, fontSize: 19, color: "var(--zen-text)" }}>
          {refundAsk === "calendar" ? "Your days are ready to see" : "Your itinerary is ready to write"}
        </h3>
        <p className="text-sm mt-2 mb-4" style={{ color: "var(--zen-sub)" }}>
          {refundAsk === "calendar" ? "Every place and every hour, the way we planned them for you." : "Every place, every hour, and every word, written for you."}
          {" "}The first time you see the detail of your trip is the moment your plan is used.
        </p>
        <label className="flex items-start gap-2 text-sm mb-4" style={{ color: "var(--zen-text)" }}>
          <input type="checkbox" checked={refundTicked} onChange={(e) => setRefundTicked(e.target.checked)} className="mt-1" data-testid="refund-ack-box" />
          <span>{refundAckText || "I understand that once I see my calendar or my itinerary, my purchase can no longer be refunded."}</span>
        </label>
        <div className="flex gap-2">
          <button onClick={() => { setRefundAsk(null); setRefundTicked(false); }} className="zen-btn zen-btn-outline flex-1 py-2.5 text-sm">Not now</button>
          <button disabled={!refundTicked} onClick={() => { const until = refundAsk; setRefundAsk(null); doGenerate(until, true); }}
            className="zen-btn zen-btn-primary flex-1 py-2.5 text-sm" data-testid="refund-ack-go">{refundAsk === "calendar" ? "Show my days" : "Write my itinerary"}</button>
        </div>
      </div>
    </div>
  );

  if (isGenerating) {
    const REVEAL_STAGE_LINE = {
      starting: "Gathering everything you told us...",
      selecting: "Weaving your picks into days that flow...",
      building: "Walking each day in our heads — so you won't zigzag across town",
      voicing: "Writing your days, one at a time — watch them arrive",
      writing: "Giving your trip its name and its story...",
      finishing: "One last read-through, with a friend's eye...",
    };
    const revealDays = genReveal?.days || [];
    const revealTitles = genReveal?.titles || {};
    const doneCount = revealDays.filter(d => revealTitles[String(d.day_number)]).length;
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--zen-bg)" }}>
        <div className="text-center px-6 w-full" style={{ maxWidth: 460 }}>
          {/* the fan folds and unfolds while the trip is built */}
          <div style={{ margin: "0 auto 20px" }}><FanLoader size={revealDays.length ? 80 : 112} /></div>
          <h3 style={{ fontFamily: '"DM Sans", system-ui, sans-serif', fontWeight: 700, fontSize: 20, color: "var(--zen-text)" }}>Your Japan is taking shape</h3>
          <p className="text-sm font-medium mt-2" style={{ color: "var(--zen-accent)" }} data-testid="gen-progress">
            {REVEAL_STAGE_LINE[genReveal?.stage] || genProgress || "Preparing..."}
          </p>

          {/* THE REVEAL — day rows appear when the calendar is locked,
              titles stream in as each day's voice lands. */}
          {revealDays.length > 0 && (
            <div className="mt-6 text-left space-y-1.5" data-testid="gen-reveal">
              {revealDays.map((d, i) => {
                const title = revealTitles[String(d.day_number)];
                const done = !!title && title !== "ready";
                return (
                  <motion.div key={d.day_number}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.06 }}
                    className="flex items-center gap-3 rounded-lg px-3.5 py-2"
                    style={{ background: "var(--zen-s1)", border: "1px solid var(--zen-border)" }}>
                    <span className="font-mono-label shrink-0" style={{ fontSize: 9, letterSpacing: "0.14em", color: "var(--zen-dim)", width: 86 }}>
                      Day {d.day_number} · {(d.city || "").slice(0, 5)}
                    </span>
                    {done ? (
                      <motion.span initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }}
                        className="text-[13px] font-medium italic truncate" style={{ color: "var(--zen-text)" }}>
                        {title}
                      </motion.span>
                    ) : title === "ready" ? (
                      <span className="text-[13px] italic" style={{ color: "var(--zen-dim)" }}>ready</span>
                    ) : (
                      <span className="reveal-dots text-[13px]" style={{ color: "var(--zen-dim)" }}>
                        <span className="animate-pulse">· · ·</span>
                      </span>
                    )}
                    {done && (
                      <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} className="ml-auto shrink-0"
                        style={{ color: "var(--zen-accent)", fontSize: 13, fontWeight: 700 }}>✓</motion.span>
                    )}
                  </motion.div>
                );
              })}
              {doneCount > 0 && doneCount === revealDays.length && (
                <p className="text-center text-[11px] pt-2" style={{ color: "var(--zen-dim)" }}>
                  All {revealDays.length} days written — finishing your trip story...
                </p>
              )}
            </div>
          )}

          <p className="font-mono-label" style={{ fontSize: 11, letterSpacing: "0.16em", marginTop: 16, color: "var(--zen-dim)" }}>
            {revealDays.length ? "We read every day twice before we hand it to you" : "About a minute — worth the wait"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "var(--zen-bg)" }}>
      <BrandHeader right={
        <button onClick={onBack}
          className="font-mono-label flex items-center gap-1.5 hover:opacity-70 transition-opacity"
          style={{ background: "none", border: "none", cursor: "pointer", fontSize: 10, letterSpacing: "0.16em", color: "var(--zen-sub)" }}>
          <ChevronLeft className="w-3.5 h-3.5" /> Back
        </button>
      } />
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
          <h1 className="font-heading text-3xl md:text-4xl tracking-tight mb-3 text-center" style={{color:'var(--zen-text)'}}>
            Your Trip at a Glance
          </h1>
          <div className="flex items-center justify-center gap-5 flex-wrap" data-testid="hero-summary">
            {tripDays > 0 && <Stat label="days" value={tripDays} />}
            <Stat label={sortedCities.length === 1 ? "city" : "cities"} value={sortedCities.length} />
            {/* one vocabulary on the whole page: places to see, tables to eat (2026-10-26: "16 places" here, 21 below) */}
            <Stat label="to see" value={activityCount} />
            {foodCount > 0 && <Stat label="to eat" value={foodCount} />}
          </div>
        </motion.div>

        {/* named versions of the trip (docs/producto 2.2 d) */}
        {sessionToken && draftId && (extractedPlaces || []).length > 0 && (
          <div className="mb-4 flex justify-center"><VersionBar draftId={draftId} sessionToken={sessionToken} onNewVersion={onNewVersion} /></div>
        )}

        {/* two views of one trip: this one, the main, and the calendar to arrange it by hand (owner, 2026-11-04) */}
        {agendaStep && (extractedPlaces || []).length > 0 && (
          <div className="mb-6">
            <ViewSwitch view="glance" busy={isCheckingCredits}
              onCalendar={() => (agendaDays ? onOpenAgenda?.() : handleGenerateClick("calendar"))} />
          </div>
        )}

        {(profilerBrief?._request_context?.requests || []).length > 0 && (
          <section className="mb-6 rounded-xl p-4" style={{ background: 'var(--zen-s1)' }} data-testid="request-choices">
            <h2 className="font-semibold mb-1">Your must-do experiences</h2>
            <p className="text-sm mb-3" style={{ color: 'var(--zen-dim)' }}>Keep these in the trip, even if you change the suggested places. Uncheck an experience only if you no longer want it.</p>
            {(profilerBrief._request_decisions || []).length > 0 && !sessionToken && (
              <p className="text-sm mb-3">Your choices are kept here. <button type="button" className="underline" onClick={onSignIn}>Sign in to use these changes</button>, then build your trip.</p>
            )}
            {profilerBrief._request_context.requests.map(request => {
              const events = (profilerBrief._request_decisions || []).filter(event => event.request_id === request.request_id);
              const included = events.length === 0 || events[events.length - 1].action !== "release";
              return <label key={request.request_id} className="flex gap-3 items-center py-2 text-sm">
                <input type="checkbox" checked={included} onChange={event => {
                  setProfilerBrief({ ...profilerBrief, _request_decisions: updateRequestChoice(
                    profilerBrief._request_decisions, profilerBrief._request_context.context_id, request.request_id, event.target.checked) });
                }} />
                <span>{request.text}</span>
              </label>;
            })}
          </section>
        )}

        {/* free is our catalog only: what they asked for that we do not hold yet, looked for once they plan (core/special_cards) */}
        {(profilerBrief?._held_for_the_plan || []).length > 0 && (
          <div className="mb-5 text-sm" data-testid="held-for-the-plan"
            style={{ padding: "10px 14px", background: "var(--zen-s1)", border: "1px solid var(--zen-border)", borderRadius: 4, color: "var(--zen-sub)" }}>
            <strong style={{ color: "var(--zen-text)" }}>Not in our catalog yet:</strong> {profilerBrief._held_for_the_plan.join(", ")}.
            {" "}We look for {profilerBrief._held_for_the_plan.length > 1 ? "them" : "it"} for you when you plan your trip.
          </div>
        )}

        {/* not signed in: the trip lives only in this browser — one line offers to keep it in their account (utils/tripDraft) */}
        {!sessionToken && (extractedPlaces || []).length > 0 && (
          <div className="mb-5 flex items-center justify-between gap-3 flex-wrap" data-testid="keep-trip-nudge"
            style={{ padding: "10px 14px", background: "var(--zen-s1)", border: "1px solid var(--zen-border)", borderRadius: 4 }}>
            <span className="text-sm" style={{ color: "var(--zen-sub)" }}>
              Planning over a few weeks? Keep this trip in your account and pick it up any time.
            </span>
            <button onClick={onSignIn} className="text-sm" style={{ background: "none", border: 0, color: "var(--zen-accent)", fontWeight: 600, cursor: "pointer" }}>
              Keep my trip
            </button>
          </div>
        )}

        {/* ========== CITY OVERVIEW HEADER ========== */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="mb-6" data-testid="ai-insights-panel">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <Lightbulb className="w-4 h-4 shrink-0" style={{color:'var(--zen-accent)'}} />
            <h2 className="text-base font-semibold tracking-tight whitespace-nowrap" style={{color:'var(--zen-text)'}}>City overview</h2>
            <span className="text-[11px] hidden sm:inline" style={{color:'var(--zen-dim)'}}>— time & AI insights per city</span>
          </div>
          {anyUnderTime && (
            <div className="mb-3 rounded-xl px-4 py-3"
              style={{background:'var(--zen-s1)', border:'1px solid var(--cat-culture-solid)'}}
              data-testid="infeasibility-warning">
              <p className="text-[13px] leading-relaxed" style={{color:'var(--zen-text)'}}>
                <span className="font-semibold" style={{color:'var(--cat-culture-solid)'}}>This trip shape looks tight.</span>{" "}
                {underCities.map(c => c.name).join(", ")}{" "}
                {underCities.length === 1
                  ? `has ~${underCities[0].availH}h of time but `
                  : "have time allocated but "}
                {underCities.length === 1 && underCities[0].isEmpty
                  ? "zero places matched your wishlist."
                  : underCities.some(c => c.isEmpty)
                    ? "some cities have no matching places."
                    : "very few places match."}
                {" "}Consider <strong>reducing days there</strong>, <strong>adding more days to a different city</strong>, or <strong>going back to pick more spots</strong>.
              </p>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="city-insight-cards">
            {cityTimeData.map((ct, idx) => (
              <CityCard key={`${ct.name}-${ct.count}-${ct.neededH}`} ct={ct} actions={(aiAnalyses[ct.name] || {}).actions || []}
                travelNext={idx < cityTimeData.length - 1 ? sortedCities[idx + 1]?.name : null} refreshing={refreshingInsights} />
            ))}
          </div>
          {/* the places sit below the city panels, and testers never scrolled to them (2026-10-08) */}
          {visiblePlaces.length > 0 && (
            <button type="button" onClick={() => document.getElementById("summary-places")?.scrollIntoView({ behavior: "smooth", block: "start" })}
              className="w-full mt-3 py-2 text-sm font-semibold flex items-center justify-center gap-1.5"
              style={{ background: "none", border: 0, color: "var(--zen-accent)", cursor: "pointer" }}
              data-testid="see-places-below">
              Your places are just below <ChevronDown className="w-4 h-4" />
            </button>
          )}
        </motion.div>

        {/* ========== FILTER BAR ========== */}
        <div id="summary-places" style={{ scrollMarginTop: 16 }} />
        {visiblePlaces.length > 0 && (
          <p className="text-xs mb-2" style={{ color: "var(--zen-sub)" }} data-testid="cards-hint">
            Tap a card to read it in full — the tick keeps it in your plan, the + adds it.
          </p>
        )}
        {visiblePlaces.length > 0 && (
          <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="summary-filters">
            <FilterPill active={cityFilter === "all"} onClick={() => setCityFilter("all")} label={`All (${extractedPlaces.length})`} />
            {sortedCities.map(c => {
              const count = extractedPlaces.filter(p => p.city === c.name).length;
              return <FilterPill key={c.name} active={cityFilter === c.name} onClick={() => setCityFilter(c.name)} label={`${c.name} (${count})`} />;
            })}
            <div className="w-px h-5 mx-1" style={{background:'var(--zen-border-h)'}} />
            <FilterPill active={showOnlyPicks} onClick={() => setShowOnlyPicks(!showOnlyPicks)} label="Your picks" icon={<Filter className="w-3 h-3" />} accent />
          </div>
        )}

        {/* what changed since our proposal, what they want to add in their own words, and the AI reading their trip
            again with it (docs/producto 2.2 a: "Add to your wishlist", the added and taken out, "Update my selection") */}
        {profilerBrief && (() => {
          const proposed = new Set((previewPicks || []).map((p) => String(p.name || "").toLowerCase()));
          const chosen = new Set((selectedPlaces || []).map((n) => String(n).toLowerCase()));
          const added = (selectedPlaces || []).filter((n) => !proposed.has(String(n).toLowerCase()));
          const removed = (previewPicks || []).map((p) => p.name).filter((n) => n && !chosen.has(String(n).toLowerCase()));
          const more = wishMore.trim();
          const changed = added.length || removed.length || more;
          const wishlist = [userInput?.text || "", more ? `I also want: ${more}` : "",
            added.length ? `Places I added to my trip: ${added.join(", ")}` : "",
            removed.length ? `Places I took out of my trip, leave them out: ${removed.join(", ")}` : ""].filter(Boolean).join("\n\n");
          return (
            <div className="mb-6 rounded-lg p-4" data-testid="selection-changes"
              style={{ background: "var(--zen-s1)", border: "1px solid var(--zen-border)" }}>
              <label className="block text-[13px] font-semibold mb-1.5" style={{ color: "var(--zen-text)" }} htmlFor="wish-more">Add to your wishlist</label>
              <textarea id="wish-more" value={wishMore} onChange={(e) => setWishMore(e.target.value)} rows={2} data-testid="wish-more"
                placeholder="A place, a dish, an experience, a link — “a sushi counter in Ginza”, “the Ghibli Museum”, “somewhere to see Fuji”"
                className="jf-field w-full wish-more" style={{ resize: "vertical" }} />
              {(added.length > 0 || removed.length > 0) && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-[13px] mt-3">
                  <div>
                    <p className="font-semibold mb-1" style={{ color: "var(--zen-text)" }}>Added ({added.length})</p>
                    <p style={{ color: "var(--zen-sub)" }}>{added.length ? added.join(", ") : "—"}</p>
                  </div>
                  <div>
                    <p className="font-semibold mb-1" style={{ color: "var(--zen-text)" }}>Taken out ({removed.length})</p>
                    <p style={{ color: "var(--zen-sub)" }}>{removed.length ? removed.join(", ") : "—"}</p>
                  </div>
                </div>
              )}
              {changed ? (
                <div className="flex items-center gap-3 flex-wrap mt-3">
                  <button onClick={() => { setWishMore(""); loadFullPipeline(wishlist); }} className="zen-btn zen-btn-primary text-sm" style={{ padding: "10px 16px" }}
                    data-testid="update-selection-btn">
                    <RefreshCw className="w-4 h-4" /> Update my selection
                  </button>
                  <span className="text-[11px]" style={{ color: "var(--zen-dim)" }}>We read your trip again with these changes.</span>
                </div>
              ) : null}
            </div>
          );
        })()}

        {/* ========== PLACES — NETFLIX STYLE BY CATEGORY ========== */}
        {visiblePlaces.length > 0 ? (
          <>
            <Section label="Our curated selection for you" count={selectedCount} icon={<Check className="w-3.5 h-3.5" />} />
            {(() => {
              const categories = ["food", "culture", "popular", "nightlife", "special", "hidden_gem"];
              const catLabels = { food: "Food & Drinks", culture: "Culture & Temples", popular: "Must-See Spots", nightlife: "Nightlife & Evening", special: "Experiences", hidden_gem: "Hidden Gems" };
              const catClasses = { food: "foodie", culture: "culture", popular: "popular", nightlife: "nightlife", special: "special", hidden_gem: "hidden" };
              // Normalize backend categories (FOODIE, CULTURE, HIDDEN GEM, etc.) to frontend keys
              const normalizeCat = (raw) => {
                if (!raw) return "popular";
                const lc = raw.toLowerCase().trim();
                if (lc === "foodie" || lc === "food") return "food";
                if (lc === "culture") return "culture";
                if (lc === "popular") return "popular";
                if (lc === "nightlife") return "nightlife";
                if (lc === "special") return "special";
                if (lc === "hidden gem" || lc === "hidden_gem" || lc === "hidden") return "hidden_gem";
                return "popular";
              };
              // Fixed display order: Must-See first, Food last
              const displayOrder = ["popular", "culture", "special", "nightlife", "hidden_gem", "food"];
              const sorted = displayOrder.filter(cat => categories.includes(cat));
              return sorted.map(cat => {
                const catPlaces = visiblePlaces.filter(p => normalizeCat(p.category) === cat);
                return <CategoryRow key={cat} label={catLabels[cat] || cat} places={catPlaces}
                  selectedPlaces={selectedPlaces} onToggle={togglePlace} onViewDetail={setDetailPlace}
                  catClass={catClasses[cat] || "popular"} momentChoice={momentChoice} onMoment={chooseMoment} />;
              });
            })()}
          </>
        ) : (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-12">
            {previewFailed ? (
              <div className="flex flex-col items-center gap-3">
                <p className="text-sm" style={{color:'var(--zen-sub)'}}>
                  We hit a snag building your selection. Try again — the AI was just slow this time.
                </p>
                <button
                  onClick={handleRetryPreview}
                  className="zen-btn zen-btn-primary px-5 py-2 text-sm"
                  data-testid="retry-preview-btn"
                >
                  Try again
                </button>
              </div>
            ) : (
              <p className="text-sm" style={{color:'var(--zen-sub)'}}>Browse the full menu to start building your trip.</p>
            )}
          </motion.div>
        )}

        {/* ========== SUGGESTIONS (always visible, dedicated card) ========== */}
        {/* Always render — even when no recs are present right now, we show
            an empty state with the "More ideas" button. The user might want
            to swap something they don't love for one of these. */}
        {(
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="mb-6" data-testid="suggested-section">
            <div className="flex items-center justify-between mb-3">
              <Section label="You might also love" icon={<Sparkles className="w-3.5 h-3.5" />} />
              <button onClick={async () => {
                // Gen-3: the Expert produces anchors during preview; there
                // is no separate "more recommendations" concept. Refreshing
                // re-runs the preview, which may surface different anchors.
                // The row this button heads renders only when recs is
                // non-empty — under Gen-3 it will typically hide itself.
                if (refreshingRecs) return;
                setRefreshingRecs(true);
                try {
                  await runAnalysisOnly({ updateActions: false });
                } finally {
                  setRefreshingRecs(false);
                }
              }}
                disabled={refreshingRecs}
                className="flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full hover:opacity-80 transition-opacity disabled:opacity-50"
                style={{ color: 'var(--zen-accent)', border: '1px solid var(--zen-border)' }}
                data-testid="refresh-recs-btn">
                <RefreshCw className={`w-3 h-3 ${refreshingRecs ? 'animate-spin' : ''}`} /> {refreshingRecs ? 'Finding ideas (~1 min)...' : 'More ideas'}
              </button>
            </div>
            {anyOverTime && (
              <p className="text-[11px] mb-2 px-1" style={{color:'var(--cat-nightlife-solid)'}}>
                Some days are already full — adding more will make them tighter.
              </p>
            )}
            {refreshingRecs ? (
              <div className="flex items-center justify-center gap-2 py-8 rounded-xl" style={{ background: 'var(--zen-s1)', border: '1px dashed var(--zen-border)' }}>
                <RefreshCw className="w-4 h-4 animate-spin" style={{ color: 'var(--zen-accent)' }} />
                <span className="text-sm" style={{ color: 'var(--zen-sub)' }}>Discovering new places for you — takes about a minute...</span>
              </div>
            ) : allRecs.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" style={{alignItems:'stretch'}}>
                {allRecs.slice(0, 8).map(rec => (
                  <RecCard key={rec.name} place={rec} onAdd={() => toggleRecommendation(rec)} onViewDetail={() => setDetailPlace(rec)} />
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center gap-2 py-8 rounded-xl text-center px-4" style={{ background: 'var(--zen-s1)', border: '1px dashed var(--zen-border)' }}>
                <span className="text-sm" style={{ color: 'var(--zen-sub)' }}>
                  No more ideas right now — tap <span className="font-medium" style={{color:'var(--zen-accent)'}}>More ideas</span> to discover alternatives you can swap in.
                </span>
              </div>
            )}
          </motion.div>
        )}

        {/* Review-panel (additive second round) removed in Step E. The
            Selector now emits 4-6 alternatives per city which the "You
            might also love" row renders — users add via that surface
            OR via the catalog browse. Less prompt complexity, fewer
            ways to drift between rounds. */}

        {/* ========== BOTTOM ACTIONS ========== */}
        <div className="pt-4 space-y-3">
          {generateError && (
            <div className="rounded-xl p-4 text-sm" style={{background:'rgba(220,75,45,0.08)',border:'1px solid var(--cat-nightlife-solid)',color:'var(--cat-nightlife-solid)'}} data-testid="generate-error">
              <p>{generateError}</p>
              <button onClick={() => { setGenerateError(null); doGenerate(); }} className="mt-3 px-4 py-1.5 rounded-lg text-xs font-medium" style={{background:'var(--cat-nightlife-solid)',color:'#fff'}} data-testid="try-again-btn">
                Try Again
              </button>
            </div>
          )}

          {/* Review is one-shot: no re-entry link. Once the user exits
              review mode (either via Update or Looks good), the only
              actions are Browse full menu and Plan my itinerary. */}

          <div className="flex gap-3 items-center justify-center sticky bottom-6">
            <div className="flex gap-3 items-center rounded-2xl px-6 py-4"
              style={{background:'var(--zen-s1)', border:'1px solid var(--zen-border)', boxShadow:'var(--shadow-card-hover)'}}>
              <button onClick={() => onNext("catalog")} className="zen-btn zen-btn-outline px-6 py-3 text-sm" data-testid="browse-menu-btn">
                <Plus className="w-4 h-4" /> Browse full menu
              </button>
              {selectedCount > 0 && (<>
                <button onClick={() => handleGenerateClick()} disabled={isCheckingCredits} className="zen-btn zen-btn-primary px-6 py-3 text-sm disabled:opacity-60" data-testid="generate-itinerary-btn">
                  {userCredits !== null && userCredits > 0 && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full mr-1" style={{background:'rgba(255,255,255,0.2)'}}>
                      {userCredits} left
                    </span>
                  )}
                  Plan my itinerary <ArrowRight className="w-4 h-4" />
                </button>
              </>)}
            </div>
          </div>
        </div>
      </div>

      <PlaceDetailModal
        place={detailPlace}
        isOpen={!!detailPlace}
        onClose={() => setDetailPlace(null)}
        isSelected={detailPlace ? selectedPlaces.includes(detailPlace.name) : false}
        onToggle={() => {
          if (detailPlace) {
            if (!selectedPlaces.includes(detailPlace.name)) {
              if (!extractedPlaces.find(p => p.name === detailPlace.name)) {
                setExtractedPlaces(prev => [...prev, detailPlace]);
              }
              setSelectedPlaces(prev => [...prev, detailPlace.name]);
            } else {
              togglePlace(detailPlace.name);
            }
          }
        }}
      />
      <PaymentModal
        isOpen={showPayment}
        onClose={() => setShowPayment(false)}
        onCreditsReady={handleCreditsReady}
        userEmail={userEmail}
        setUserEmail={setUserEmail}
      />
    </div>
  );
};

/* ========== CITY CARD ========== */
const CityCard = ({ ct, actions, travelNext, refreshing }) => {
  const pct = Math.min(100, Math.round(ct.fill * 100));
  const room = Math.max(0, ct.target - ct.activityCount);
  const borderColor = ct.isOver
    ? 'var(--cat-nightlife-solid)'
    : ct.isUnder
      ? 'var(--cat-culture-solid)'
      : 'var(--zen-border)';
  return (
    <div className="rounded-2xl overflow-hidden transition-shadow hover:shadow-md"
      style={{background:'var(--zen-s1)', border: `1.5px solid ${borderColor}`}}
      data-testid={`insight-card-${ct.name}`}>
      <div className="px-5 pt-4 pb-2">
        <div className="flex items-baseline justify-between">
          <h3 className="text-xl font-bold tracking-tight" style={{color:'var(--zen-text)', fontFamily:'var(--font-heading)'}}>{ct.name}</h3>
          <span className="text-sm font-semibold" style={{color:'var(--zen-accent)'}}>
            {ct.days}d · {ct.activityCount} to see{ct.foodCount > 0 ? ` · ${ct.foodCount} to eat` : ''}
          </span>
        </div>
        <div className="mt-2">
          <div className="flex items-center justify-between text-[11px] mb-1">
            <span style={{color:'var(--zen-dim)'}}>About {ct.target} fit these days, meals between</span>
            <span className="font-semibold" style={{color: ct.isOver ? 'var(--cat-nightlife-solid)' : ct.isUnder ? 'var(--cat-culture-solid)' : ct.fill >= 0.8 ? 'var(--cat-foodie-solid)' : 'var(--cat-culture-solid)'}}>
              {ct.isEmpty
                ? 'No places'
                : ct.isOver
                  ? 'Very full'
                  : ct.isUnderfilled
                    ? 'Too few'
                    : ct.activityCount > ct.target ? `Full — we keep the best ${ct.target}`
                    : ct.fill >= 0.8 ? 'Well filled' : `Room for ${room} more`}
            </span>
          </div>
          <div className="w-full rounded-full h-2.5" style={{background:'var(--zen-border-h)'}}>
            <div className="h-2.5 rounded-full transition-all duration-500" style={{background: ct.isOver ? 'var(--cat-nightlife-solid)' : ct.isUnder ? 'var(--cat-culture-solid)' : 'var(--zen-accent)', width: `${pct}%`}} />
          </div>
        </div>
        {ct.isUnder && (
          <p className="text-[11px] mt-2 leading-snug" style={{color:'var(--cat-culture-solid)'}}>
            {ct.isEmpty
              ? `No places here matched your wishlist — consider dropping ${ct.name} or adding a day elsewhere.`
              : `Only ${ct.count} place${ct.count === 1 ? '' : 's'} for ${ct.days} day${ct.days === 1 ? '' : 's'} — pick more spots or shorten this city.`}
          </p>
        )}
      </div>
      {refreshing ? (
        <div className="px-5 pb-4 pt-3 flex items-center gap-2" style={{borderTop:'1px solid var(--zen-border)'}}>
          <RefreshCw className="w-3 h-3 animate-spin" style={{color:'var(--zen-accent)'}} />
          <span className="text-[12px]" style={{color:'var(--zen-sub)'}}>Refreshing insights...</span>
        </div>
      ) : actions.length > 0 && (
        <div className="px-5 pb-4 pt-3 space-y-2.5" style={{borderTop:'1px solid var(--zen-border)'}}>
          {actions.slice(0, 3).map((action, i) => {
            // Bold place names in the text (capitalized multi-word names)
            const parts = action.split(/(\b[A-Z][a-zA-Zō-]+(?:\s+(?:no\s+)?[A-Z][a-zA-Zō-]+)*(?:\s+(?:Temple|Shrine|Market|Castle|Museum|Tower|Park|Garden|Alley|Street|Station|District|Bridge|Falls|Hall))?)/g);
            return (
              <div key={i} className="flex items-start gap-2.5">
                {/* Uniform bullet — same dot for all three */}
                <span className="mt-[7px] shrink-0 rounded-full w-1.5 h-1.5"
                  style={{background:'var(--zen-accent)'}} aria-hidden="true" />
                <span className="text-[13px] leading-relaxed"
                  style={{color:'var(--zen-text)'}}>
                  {parts.map((part, j) => {
                    const isPlace = /^[A-Z][a-zA-Zō-]+(?:\s+(?:no\s+)?[A-Z][a-zA-Zō-]+)+/.test(part) ||
                      /(?:Temple|Shrine|Market|Castle|Museum|Tower|Park|Garden|Alley|Street|Station|District)$/i.test(part);
                    return isPlace
                      ? <strong key={j} style={{fontWeight:600}}>{part}</strong>
                      : <span key={j}>{part}</span>;
                  })}
                </span>
              </div>
            );
          })}
        </div>
      )}
      {travelNext && ct.travelOut > 0 && (
        <div className="px-5 py-2 text-[10px] flex items-center gap-1"
          style={{background:'var(--zen-s2)', color:'var(--zen-dim)', borderTop:'1px solid var(--zen-border)'}}>
          → {ct.travelOut}h travel to {travelNext}
        </div>
      )}
    </div>
  );
};

/* ========== NETFLIX-STYLE CATEGORY ROW ========== */
const CategoryRow = ({ label, places, selectedPlaces, onToggle, onViewDetail, catClass, momentChoice, onMoment }) => {
  const scrollRef = useRef(null);
  const [canScrollL, setCanScrollL] = useState(false);
  const [canScrollR, setCanScrollR] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollL(el.scrollLeft > 10);
    setCanScrollR(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  }, []);

  useEffect(() => { checkScroll(); }, [places.length, checkScroll]);

  const scroll = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 280, behavior: 'smooth' });
    setTimeout(checkScroll, 350);
  };

  if (!places.length) return null;

  // Adaptive card width: wider when fewer items to fill space
  const cardWidth = places.length === 1 ? 220 : places.length <= 3 ? 190 : 160;

  return (
    <div className="mb-5" data-testid={`category-row-${catClass}`}>
      <div className="flex items-center gap-2 mb-2.5 px-1">
        <span className={`w-2 h-2 rounded-full`} style={{background: `var(--cat-${catClass}-solid, var(--zen-accent))`}} />
        <span className="text-sm font-semibold tracking-tight" style={{color:'var(--zen-text)'}}>{label}</span>
        <span className="text-[11px]" style={{color:'var(--zen-dim)'}}>({places.length})</span>
      </div>
      <div className="relative group">
        {canScrollL && (
          <button onClick={() => scroll(-1)}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full flex items-center justify-center transition-all hover:scale-110"
            style={{background:'var(--zen-bg)', border:'1px solid var(--zen-border)', boxShadow:'0 2px 8px rgba(0,0,0,0.15)'}}
            aria-label="Scroll left">
            <ChevronLeft className="w-5 h-5" style={{color:'var(--zen-text)'}} />
          </button>
        )}
        <div ref={scrollRef} onScroll={checkScroll}
          className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide"
          style={{scrollSnapType:'x mandatory', scrollbarWidth:'none', msOverflowStyle:'none'}}>
          {places.map(place => (
            <div key={place.name} className="shrink-0" style={{width: cardWidth, scrollSnapAlign:'start'}}>
              <PlaceThumb place={place} selected={selectedPlaces.includes(place.name)}
                onToggle={() => onToggle(place.name)} onViewDetail={() => onViewDetail(place)}
                momentChoice={momentChoice} onMoment={onMoment} />
            </div>
          ))}
        </div>
        {canScrollR && (
          <button onClick={() => scroll(1)}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full flex items-center justify-center transition-all hover:scale-110"
            style={{background:'var(--zen-bg)', border:'1px solid var(--zen-border)', boxShadow:'0 2px 8px rgba(0,0,0,0.15)'}}
            aria-label="Scroll right">
            <ChevronRight className="w-5 h-5" style={{color:'var(--zen-text)'}} />
          </button>
        )}
      </div>
    </div>
  );
};
const Stat = ({ label, value }) => (
  <div className="flex items-baseline gap-1">
    <span className="text-xl font-semibold" style={{color:'var(--zen-text)'}}>{value}</span>
    <span className="text-[11px]" style={{color:'var(--zen-dim)'}}>{label}</span>
  </div>
);
const FilterPill = ({ active, onClick, label, icon, accent }) => (
  <button onClick={onClick} className="flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-medium transition-all"
    style={{ background: active ? (accent ? 'var(--cat-yourpick)' : 'var(--zen-text)') : 'transparent', color: active ? (accent ? '#fff' : 'var(--zen-bg)') : 'var(--zen-sub)', border: `1px solid ${active ? 'transparent' : 'var(--zen-border-h)'}` }}>
    {icon}{label}
  </button>
);
const Section = ({ label, count, icon }) => (
  <div className="flex items-center gap-2 mb-3">
    <span style={{color:'var(--zen-accent)'}}>{icon}</span>
    <span className="text-[13px] font-medium" style={{color:'var(--zen-text)'}}>{label}</span>
    {count !== undefined && <span className="text-[11px]" style={{color:'var(--zen-dim)'}}>({count})</span>}
    <div className="h-px flex-1" style={{background:'var(--zen-border)'}} />
  </div>
);

const PlaceThumb = ({ place, selected, onToggle, onViewDetail, dashed, momentChoice, onMoment }) => {
  const rawCat = place.category || "popular";
  const isUserPick = place.from_wishlist;
  // Off-catalog special card: the AI found and vetted this specifically
  // for a wish our catalog couldn't serve. Worn proudly.
  const isSpecial = place.from_catalog === false;
  const cat = CAT_CLASS_THUMB[rawCat] ? rawCat : "popular";
  return (
    <motion.div layout initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: selected ? 1 : 0.7, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }} transition={{ duration: 0.2 }} onClick={onViewDetail}
      className="rounded-xl overflow-hidden cursor-pointer transition-shadow hover:shadow-md"
      style={{ background: 'var(--zen-s1)', border: `1.5px ${dashed ? 'dashed' : 'solid'} ${!selected ? 'var(--zen-border)' : isUserPick ? 'var(--cat-yourpick)' : 'var(--zen-accent)'}` }}
      data-testid={`summary-card-${place.name?.replace(/\s+/g, '-')}`}>
      <div className="aspect-[3/2] overflow-hidden relative" style={{background:'var(--zen-s2)'}}>
        {place.image ? (
          <><img src={placeImg(place.image)} alt={place.name} className="w-full h-full object-cover transition-all" loading="lazy" /><PhotoCredit source={place.image_source} credit={place.image_credit} /></>
        ) : (
          /* Brand tile — the same obsidian card the itinerary and PDF use
             for a place we don't stock a photo of. Never a broken image. */
          <div className="w-full h-full flex flex-col items-center justify-center px-3 text-center" style={{ background: '#1E1A15' }}>
            <FanMark size={18} toriiColor="#F5F5F0" title="" style={{ marginBottom: 6 }} />
            <span className="leading-tight" style={{ color: '#F5F5F0', fontFamily: '"Unbounded", sans-serif', fontWeight: 700, fontSize: 10 }}>{place.name}</span>
            <span className="font-mono-label mt-1" style={{ fontSize: 7, letterSpacing: '0.18em', color: '#E82529' }}>
              {isSpecial ? '★ Found for you' : '★ Your pick'}
            </span>
          </div>
        )}
        <span className={`zen-badge ${CAT_CLASS_THUMB[cat] || 'popular'}`} style={{fontSize:'7px',padding:'2px 6px',top:6,left:6}}>
          {CAT_LABEL[cat] || 'POPULAR'}
        </span>
        {isSpecial && (
          <span className="absolute bottom-1.5 left-1.5 text-[7px] px-1.5 py-0.5 rounded-full font-semibold uppercase flex items-center gap-0.5"
            style={{background:'var(--zen-accent)',color:'#fff'}}
            title="Not in our catalog — found and vetted specifically for your wishlist">
            ✨ found for you
          </span>
        )}
        {isUserPick && selected && (
          <span className="absolute bottom-1.5 right-1.5 text-[7px] px-1.5 py-0.5 rounded-full font-semibold uppercase"
            style={{background:'var(--cat-yourpick)',color:'#fff'}}>your pick</span>
        )}
        {/* what the place is to their trip, said in words (docs/producto 2.2 a: "Add to my trip" / "In your trip ✓") */}
        <button type="button" className="absolute top-1.5 right-1.5 h-5 px-1.5 rounded-full flex items-center gap-0.5 hover:scale-105 transition-transform text-[9px] font-semibold text-white"
          onClick={(e) => { e.stopPropagation(); onToggle(); }}
          style={{ background: selected ? 'var(--zen-accent)' : 'rgba(0,0,0,0.45)', border: selected ? 'none' : '1.5px solid rgba(255,255,255,0.6)' }}
          aria-pressed={selected} data-testid={`toggle-${place.name?.replace(/\s+/g, '-')}`}>
          {selected ? <><Check className="w-3 h-3" /> In your trip</> : <><Plus className="w-3 h-3" /> Add to my trip</>}
        </button>
      </div>
      <div className="px-2.5 py-2" style={{minHeight:'60px'}}>
        <h4 className="font-medium text-[12px] leading-tight truncate" style={{color:'var(--zen-text)', opacity: selected ? 1 : 0.7}}>{place.name}</h4>
        {place.why ? (
          <p className="text-[10px] leading-snug line-clamp-2 mt-0.5" style={{color:'var(--zen-sub)', opacity: selected ? 1 : 0.6}}>
            {place.why.replace(/^You asked for[^→]*→\s*/i, '').replace(/^Because you[^,]*,\s*/i, '')}
          </p>
        ) : (place.punchline || place.description) ? (
          <p className="text-[10px] leading-snug line-clamp-2 mt-0.5" style={{color:'var(--zen-sub)', opacity: selected ? 1 : 0.6}}>{place.punchline || place.description}</p>
        ) : null}
        <div className="flex items-center gap-1.5 mt-0.5">
          <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(place.name + ", " + (place.city || "Japan"))}`}
            target="_blank" rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-[9px] hover:underline transition-opacity hover:opacity-70"
            style={{color:'var(--zen-dim)'}}
            data-testid={`maps-link-${place.name?.replace(/\s+/g, '-')}`}>
            {place.city}
          </a>
          <span className="text-[9px] font-semibold" style={{ color: 'var(--zen-accent)' }}>More ›</span>
          {place.moments && onMoment && (() => {
            // a place as good at dawn as after dark: one tap plans it at the other moment, by code, at build time
            const now = (momentChoice || {})[place.name] || place.moments.planned;
            const other = now === "night" ? "dawn" : "night";
            return (
              <button type="button" onClick={(e) => { e.stopPropagation(); onMoment(place.name, other === place.moments.planned ? null : other); }}
                className="text-[9px] font-semibold underline" style={{ color: 'var(--zen-accent)', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                data-testid={`moment-${place.name?.replace(/\s+/g, '-')}`}>
                {now === "night" ? "After dark · prefer the morning?" : "Morning · prefer after dark?"}
              </button>
            );
          })()}
          {place.duration && (
            <span className="text-[9px] flex items-center gap-0.5 ml-auto" style={{color: selected ? 'var(--zen-accent)' : 'var(--zen-dim)'}}>
              <Clock className="w-2.5 h-2.5" />{formatDuration(place.duration)}
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );
};

/* ========== RECOMMENDATION CARD (for "You might also love") ========== */
const RecCard = ({ place, onAdd, onViewDetail }) => {
  const cat = place.category || place.type || "";
  return (
    <div className="rounded-xl overflow-hidden cursor-pointer transition-shadow hover:shadow-md flex flex-col h-full"
      onClick={onViewDetail}
      style={{ background: 'var(--zen-s1)', border: '1.5px dashed var(--zen-border)' }}
      data-testid={`rec-card-${place.name?.replace(/\s+/g, '-')}`}>
      <div className="aspect-[3/2] overflow-hidden relative shrink-0" style={{background:'var(--zen-s2)'}}>
        {place.image ? (
          <><img src={placeImg(place.image)} alt={place.name} className="w-full h-full object-cover" loading="lazy" /><PhotoCredit source={place.image_source} credit={place.image_credit} /></>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center px-3 text-center"
            style={{background:'linear-gradient(135deg, var(--zen-s2) 0%, var(--zen-accent) 120%)'}}>
            <Sparkles className="w-4 h-4 mb-1" style={{color:'rgba(255,255,255,0.7)'}} />
            <span className="text-[9px] font-medium leading-tight" style={{color:'rgba(255,255,255,0.85)'}}>{place.name}</span>
          </div>
        )}
        <span className={`zen-badge ${CAT_CLASS_THUMB[cat] || 'popular'}`} style={{fontSize:'7px',padding:'2px 6px',top:6,left:6}}>
          {CAT_LABEL[cat] || 'POPULAR'}
        </span>
      </div>
      <div className="px-2.5 py-2 flex flex-col flex-1">
        <h4 className="font-medium text-[12px] leading-tight truncate" style={{color:'var(--zen-text)'}}>{place.name}</h4>
        {(place.punchline || place.description) && (
          <p className="text-[10px] leading-snug line-clamp-2 mt-0.5" style={{color:'var(--zen-sub)'}}>{place.punchline || place.description}</p>
        )}
        <div className="flex items-center gap-1.5 mt-0.5 mb-1.5">
          <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(place.name + ", " + (place.city || "Japan"))}`}
            target="_blank" rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-[9px] hover:underline transition-opacity hover:opacity-70"
            style={{color:'var(--zen-dim)'}}>
            {place.city}
          </a>
          {place.duration && (
            <span className="text-[9px] flex items-center gap-0.5 ml-auto" style={{color:'var(--zen-dim)'}}>
              <Clock className="w-2.5 h-2.5" />{formatDuration(place.duration)}
            </span>
          )}
        </div>
        <button onClick={(e) => { e.stopPropagation(); onAdd(); }}
          className="w-full flex items-center justify-center gap-1 text-[10px] font-medium py-1.5 rounded-lg transition-all hover:opacity-80 mt-auto"
          style={{ background: 'var(--zen-accent)', color: '#fff' }}
          data-testid={`add-rec-${place.name?.replace(/\s+/g, '-')}`}>
          <Plus className="w-3 h-3" /> Add to my plan
        </button>
      </div>
    </div>
  );
};

export default SummaryPage;
